/**
 * maintenance.ts
 *
 * Pure maintenance-window domain logic.
 *
 * This module is deliberately free of Vue, Pinia and Firebase imports: the
 * decision "is the site in maintenance right now" is arithmetic over a
 * window and a clock, and keeping it that way means it can be tested
 * without a browser, a store, or a network round trip.
 */

/** A maintenance window as configured by an operator. */
export interface MaintenanceWindow {
  /** Master switch. When false the window is irrelevant. */
  enabled: boolean
  /** Inclusive start, or null when the maintenance starts immediately. */
  from: Date | null
  /** Inclusive end, or null when the maintenance has no planned end. */
  to: Date | null
}

/** The raw strings a remote configuration service hands back. */
export interface RawMaintenanceWindow {
  enabled: boolean
  from: string
  to: string
}

/** A window that is never active. Used whenever configuration is missing. */
export const INACTIVE_MAINTENANCE_WINDOW: MaintenanceWindow = {
  enabled: false,
  from: null,
  to: null,
}

/**
 * Converts raw remote-configuration values into a maintenance window.
 *
 * Blank and unparseable timestamps become null rather than an error: an
 * operator typo must not leave the app stuck with an Invalid Date that
 * every comparison silently answers false to.
 *
 * An inverted window (end before start) is discarded entirely, because such
 * a window can never be active and would quietly cancel a maintenance the
 * operator believed they had scheduled.
 *
 * @param raw - The enabled flag plus the two timestamp strings.
 * @returns The parsed window.
 */
export function parseMaintenanceWindow(raw: RawMaintenanceWindow): MaintenanceWindow {
  const from = parseTimestamp(raw.from)
  const to = parseTimestamp(raw.to)

  if (from && to && to.getTime() < from.getTime()) {
    return { enabled: raw.enabled, from: null, to: null }
  }

  return { enabled: raw.enabled, from, to }
}

/**
 * Reports whether the window covers the given instant.
 *
 * Both bounds are inclusive. A null bound is treated as open-ended, so a
 * window with neither bound is active for as long as the flag is on.
 *
 * @param window - The configured window.
 * @param nowMs - The current time in milliseconds since the epoch.
 * @returns True when the site should show the maintenance page.
 */
export function isMaintenanceActive(window: MaintenanceWindow, nowMs: number): boolean {
  if (!window.enabled) {
    return false
  }
  if (window.from && nowMs < window.from.getTime()) {
    return false
  }
  if (window.to && nowMs > window.to.getTime()) {
    return false
  }

  return true
}

/**
 * The timezone every maintenance time is shown in.
 *
 * Operators and customers are all in Vietnam, so a window is meaningless
 * rendered in the viewer's local timezone - a user abroad would be told the
 * site comes back at a time nobody scheduled.
 */
const VIETNAM_TIMEZONE = 'Asia/Ho_Chi_Minh'

/**
 * Renders a maintenance window as the Vietnamese line shown to users.
 *
 * Returns an empty string when no end is planned: an open-ended window
 * gives the user nothing to wait for, and showing only a start time reads
 * as though the outage had just begun.
 *
 * @param from - Start of the window, or null.
 * @param to - End of the window, or null.
 * @returns The line to display, or an empty string when there is nothing
 *   useful to say.
 */
export function formatMaintenanceWindow(from: Date | null, to: Date | null): string {
  if (!to) {
    return ''
  }
  if (!from) {
    return `Dự kiến kết thúc: ${formatTime(to)} ${formatDate(to)}`
  }
  if (formatDate(from) === formatDate(to)) {
    return `Dự kiến: ${formatTime(from)} - ${formatTime(to)} ${formatDate(to)}`
  }

  return `Dự kiến: ${formatTime(from)} ${formatDate(from)} - ${formatTime(to)} ${formatDate(to)}`
}

/** Vietnam's fixed UTC offset. The country has observed GMT+7 since 1975. */
const VIETNAM_OFFSET = '+07:00'

/**
 * Stamps a calendar day and a wall-clock time with Vietnam's offset.
 *
 * The operator picks both on a calendar and a clock that mean Vietnam time,
 * so the offset is fixed rather than taken from the browser: an admin working
 * from another country must still schedule the window the rest of the team
 * sees.
 *
 * @param day - The calendar day, read from its local date parts, or null.
 * @param time - The wall-clock time as "HH:mm"; empty means midnight.
 * @returns An RFC 3339 timestamp, or an empty string when no day was given.
 */
export function toVietnamIso(day: Date | null, time: string): string {
  if (!day) {
    return ''
  }

  const [hour, minute] = splitTime(time)

  return (
    `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}` +
    `T${pad(hour)}:${pad(minute)}:00${VIETNAM_OFFSET}`
  )
}

/**
 * Splits a stored timestamp back into the day and time to show.
 *
 * The value is rendered in Vietnam time whatever offset it carries, so a
 * window written by hand in UTC still reads as the hour it will actually
 * take effect.
 *
 * @param value - An RFC 3339 timestamp, or an empty string.
 * @returns The calendar day and "HH:mm", both empty when there is nothing to
 *   show or the value cannot be parsed.
 */
export function fromVietnamIso(value: string): { day: Date | null, time: string } {
  const parsed = parseTimestamp(value)
  if (!parsed) {
    return { day: null, time: '' }
  }

  const parts = vietnamParts(parsed)

  return {
    day: new Date(parts.year, parts.month - 1, parts.day),
    time: `${pad(parts.hour)}:${pad(parts.minute)}`,
  }
}

/**
 * Normalises a Vite mode into the environment the maintenance switch is
 * keyed by.
 *
 * The Remote Config condition matches the exact string "dev", but Vite
 * reports three different modes: "production" for the release build, "dev"
 * for `npm run build-dev`, and "development" for the local dev server.
 * Passing the mode through verbatim meant the condition never fired locally.
 *
 * Anything unrecognised maps to dev, which is the safe direction: a mode this
 * function has not been taught about must not pick up production's switch.
 *
 * @param mode - The value of `import.meta.env.MODE`.
 * @returns Either "production" or "dev".
 */
export function environmentSignal(mode: string): string {
  return mode === 'production' ? 'production' : 'dev'
}

// =============================================================================
// Helper Functions
// =============================================================================

/** Parses one timestamp, returning null for blank or invalid input. */
function parseTimestamp(value: string): Date | null {
  const trimmed = value.trim()
  if (trimmed === '') {
    return null
  }

  const parsed = new Date(trimmed)

  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/** Formats the time of day in Vietnam as HH:mm. */
function formatTime(value: Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: VIETNAM_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(value)
}

/** Formats the calendar date in Vietnam as DD/MM/YYYY. */
function formatDate(value: Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: VIETNAM_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(value)
}

/** Splits "HH:mm" into numbers, treating anything unusable as midnight. */
function splitTime(time: string): [number, number] {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim())
  if (!match) {
    return [0, 0]
  }

  return [Number(match[1]), Number(match[2])]
}

/** Reads an instant's date and time parts as they appear in Vietnam. */
function vietnamParts(value: Date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: VIETNAM_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(value)

  const read = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0)

  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour'),
    minute: read('minute'),
  }
}

/** Left-pads a number to two digits. */
function pad(value: number): string {
  return String(value).padStart(2, '0')
}
